"use client";

import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, TrendingUp, Navigation, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function Localdomination() {
  const shouldReduceMotion = useReducedMotion();
  const localHighlights = [{
    title: "Google Maps 3-Pack",
    desc: "Strong visibility in Google's local map pack results"
  }, {
    title: "Hyper-Local Strategy",
    desc: "Target high-intent searchers in your exact service areas"
  }, {
    title: "Directory & NAP Sync",
    desc: "Consistent, accurate name, address and phone details across directories"
  }];
  const mapLocations = [{
    name: "Local Business A",
    distance: "0.2 mi"
  }, {
    name: "Local Business B",
    distance: "1.1 mi"
  }, {
    name: "Local Business C",
    distance: "2.4 mi"
  }];
  return <section id="localdominationhero" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-28 overflow-hidden isolate">
      {/* Decorative ambient gradients */}
      <div className="absolute top-0 right-0 -z-10 size-96 bg-secondary/30 rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 size-80 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Heading & Content */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start">
            <Badge variant="secondary" className="mb-4 px-3 py-1.5 text-xs font-semibold text-primary rounded-full border border-primary/20">
              <MapPin className="size-3.5 mr-1 text-accent" />
              Local SEO Consultant & Search Strategy
            </Badge>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Local SEO
            </h1>
            <p className="mt-2 text-xl sm:text-2xl font-bold text-foreground">
              Own Your Local Search Rankings
            </p>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              As an independent local SEO consultant, I help ambitious businesses dominate Google Maps, secure top 3-pack search listings, and turn high-intent local searches into paying customers. From geo-targeted relevance to directory accuracy, every strategy is built to outrank nearby competitors and establish commanding local search dominance.
            </p>

            {/* Quick Stat Highlight */}
            <div className="mt-6 p-4 rounded-xl bg-card border border-border w-full max-w-2xl flex items-center gap-4 shadow-xs">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-xl">
                <MapPin className="size-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-card-foreground">
                  Local search performance is closely monitored to capture ready-to-buy customers at the right moment.
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Target searchers at the exact moment they are ready to buy locally.
                </p>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button size="lg" asChild className="rounded-md font-semibold text-base shadow-md">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" asChild className="rounded-md font-medium text-base">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>

            <Separator className="my-8 w-full max-w-2xl" />

            {/* Key Value Points */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-2xl">
              {localHighlights.map((item, i) => <div key={i} data-index={i} className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5 text-foreground font-semibold text-sm">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-5.5">{item.desc}</p>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Visual Local SEO Proof & Map Mock */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none rounded-2xl bg-card text-card-foreground border border-border p-6 shadow-xl">
              
              {/* Header Profile */}
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="relative size-12 rounded-full overflow-hidden border-2 border-primary shrink-0">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" alt="Yassine Essaadi - Local SEO Consultant" className="size-full object-cover" loading="lazy" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-sm text-card-foreground">Yassine Essaadi</h2>
                    <p className="text-xs text-muted-foreground">Independent SEO Consultant</p>
                  </div>
                </div>
                <Badge variant="outline" className="bg-secondary/50 text-secondary-foreground border-secondary text-xs">
                  Independent Consultant
                </Badge>
              </div>

              {/* Local 3-Pack Visualizer */}
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <span>Google Local Pack Performance</span>
                  <span className="text-primary flex items-center gap-1">
                    <TrendingUp className="size-3" /> Top Rankings
                  </span>
                </div>

                {mapLocations.map((loc, i) => <div key={i} data-index={i} className="p-3.5 rounded-xl bg-background border border-border hover:border-primary/50 transition-colors flex items-center justify-between shadow-xs">
                    <div className="flex items-center gap-3">
                      <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground flex items-center gap-1">
                          {loc.name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                          <span>Distance: {loc.distance}</span>
                        </div>
                      </div>
                    </div>
                  </div>)}

                <p className="text-center text-xs text-muted-foreground pt-1">
                  Illustrative example — not actual search results.
                </p>
              </div>

              {/* Footer Note */}
              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <Navigation className="size-3.5 text-accent" /> Tailored Local SEO Strategy
                </span>
                <span>Swindon & UK Wide</span>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}